import {useContext, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {atomButtonShapes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import TopbarUser, {topbarUserHeadingTags} from '@s-ui/react-topbar-user'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import {
  imageUrlAdapterImageTypes,
  imageUrlAdapterTransformTypes,
  useImageUrlAdapter
} from '@adv-mt/mt-hook-use-image-url-adapter'
import SharedBrandLogo from '@adv-mt/mt-shared-brand-logo'
import IconChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDown'
import TitleBasic, {titleBasicSizes} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Button, BUTTON_INTENTS, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'

import {MAX_CONTENT_WIDTHS} from './config.js'
import {icons} from './shared-topbar-icons.js'

const DESIGNS = {
  default: 'default',
  fullWidth: 'fullWidth'
}

const TEST_ID_SHARED_TOPBAR = 'testIdSharedTopbar'
const CTA_TEST_ID = 'shared-topbar-cta'
const CTA_LOGIN_TEST_ID = 'shared-topbar-cta-login'
const CTA_SHARED_PROPS = {
  intent: BUTTON_INTENTS.PRIMARY,
  shape: BUTTON_SHAPES.CIRCLE,
  size: BUTTON_SIZES.MEDIUM
}
const badgeCounterMax = 100
const badgeCounterExceeded = '+99'

const GET_ALL_NOTIFICATIONS_STREAM_USE_CASE = 'get_all_notifications_messaging_use_case'

const BASE_CLASS = 'mt-SharedTopbar'

const getSharedBrandIcon = () => (
  <img src="https://s.ccdn.es/images/logo-coches-small.svg" className={`${BASE_CLASS}-brand-icon`} />
)

const getSharedBrandLogo = ({alt}) => <SharedBrandLogo alt={alt} />

const ACCESSIBLE_BADGE_LABEL_LITERAL = 'COMMON.PLURALS.NOTIFICATION'
const ACCESSIBLE_BADGE_LABEL_UNKNOWN_NOTIFICATIONS_LITERAL = 'COMMON.YOU_HAVE_NOTIFICATIONS'

const NOTIFICATION_TYPES = {
  PENDING_PTA: 'pendingPta',
  UNREAD_MESSAGES: 'unreadMessages',
  UNSEEN_SAVED_SEARCHES: 'unseenSavedSearches'
}

const SharedTopbar = ({
  brandHeadingTag,
  brandLogoAlt,
  customContent,
  design = DESIGNS.default,
  elementsToKeepScrollOnToggleMenu,
  isHeaderFlush = false,
  linkFactory,
  mainNavigationItems,
  maxContentWidth = MAX_CONTENT_WIDTHS.MEDIUM,
  navButton,
  navCTASecondary,
  showBrandIcon = false,
  showNavUserOnDesktop = true,
  showToggleNavOnMobile = true,
  title,
  withLoginButton = true
}) => {
  const {domain, i18n} = useContext(Context)
  const {user} = useContext(AuthenticationContext)
  const [isOpen, setIsOpen] = useState(false)

  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const {cTA, cTALogin, DEFAULT_NOTIFICATIONS, main, user: userConfig} = config.get('navigation')

  const {login: loginScreenId} = config.get('LOGIN_SCREENSET_ID_MAPPING')
  const {HIT_ORIGINS} = config.get('tracking')

  const _getNavigationItems = ({main}) => {
    return main
  }
  const navigationItems = mainNavigationItems || _getNavigationItems({main})
  const brandUrl = config.get('HOME_URL')

  const baseClassNames = cx(BASE_CLASS, {
    'is-open': isOpen,
    [`${BASE_CLASS}--fullWidth`]: design === DESIGNS.fullWidth,
    [`${BASE_CLASS}--maxContent-${maxContentWidth}`]: design === DESIGNS.fullWidth && maxContentWidth,
    [`${BASE_CLASS}--headerFlush`]: isHeaderFlush,
    [`${BASE_CLASS}--withSecondaryCTA`]: Boolean(navCTASecondary)
  })

  const BRAND = {
    icon: getSharedBrandIcon,
    image: () => getSharedBrandLogo({alt: brandLogoAlt}),
    url: brandUrl,
    ...(brandHeadingTag && {headingTag: brandHeadingTag})
  }

  const [notifications, setNotifications] = useState({
    [NOTIFICATION_TYPES.PENDING_PTA]: DEFAULT_NOTIFICATIONS,
    [NOTIFICATION_TYPES.UNREAD_MESSAGES]: DEFAULT_NOTIFICATIONS,
    [NOTIFICATION_TYPES.UNSEEN_SAVED_SEARCHES]: DEFAULT_NOTIFICATIONS
  })

  const {track} = useAnalyticsTrack()

  const {triggerLoginModal} = useContext(LoginModalContext)

  useMount(async () => {
    const {HAS_NOTIFICATIONS_ENABLED} = config.get('notifications')

    const notifications = HAS_NOTIFICATIONS_ENABLED
      ? await domain.get(GET_ALL_NOTIFICATIONS_STREAM_USE_CASE).execute()
      : {}

    setNotifications({
      [NOTIFICATION_TYPES.PENDING_PTA]: notifications.pendingPta || DEFAULT_NOTIFICATIONS,
      [NOTIFICATION_TYPES.UNREAD_MESSAGES]: notifications.unreadMessages || DEFAULT_NOTIFICATIONS,
      [NOTIFICATION_TYPES.UNSEEN_SAVED_SEARCHES]: notifications.unseenSavedSearches || DEFAULT_NOTIFICATIONS
    })
  })

  const _shouldDisplayNavUser = ({hideNavUserInMobile}) => !(isMobile && hideNavUserInMobile)

  const _shouldBePrinted = ({hideOnMobile}) => !(isMobile && hideOnMobile)

  const _getText = text => i18n.t(text)

  const _getAccessibleBadgeLabelLiteral = (id, notificationsData) => {
    const {count} = notificationsData || {}
    const totalNotifications = count || notifications[id]?.count || 0
    return totalNotifications > 0
      ? `(${i18n.t(ACCESSIBLE_BADGE_LABEL_LITERAL, totalNotifications)})`.toLowerCase()
      : `(${i18n.t(ACCESSIBLE_BADGE_LABEL_UNKNOWN_NOTIFICATIONS_LITERAL)})`.toLowerCase()
  }

  /**
   * Map data for sui-topbar-user sections
   */
  const _mapData = data => {
    const {
      highlight,
      icon,
      id,
      isExternal,
      notifications: notificationsData,
      text,
      trackingData,
      url,
      loginModalAction
    } = data

    const hasIdAndNotifications = id && typeof notificationsData !== 'undefined'

    notifications.unseenSavedSearches >= badgeCounterMax &&
      setNotifications({
        ...notifications,
        unseenSavedSearches: badgeCounterExceeded
      })

    const hasTrackingData = Boolean(trackingData && Object.keys(trackingData).length)
    const hasLoginModalAction = Boolean(loginModalAction)

    // Shape can be included in the config.js when the experiment is over and removed from here. It will be included in the cTA object
    return {
      ...(hasIdAndNotifications && {
        accessibleBadgeLabel: _getAccessibleBadgeLabelLiteral(id, notificationsData)
      }),
      shape: atomButtonShapes.CIRCULAR,
      text: _getText(text),
      url: i18n.t(url),
      highlight,
      ...(icon && {icon: icons[icon]}),
      ...(hasIdAndNotifications && {
        notifications: notifications[id] || notificationsData
      }),
      ...(isExternal && {target: '_blank'}),
      ...(hasTrackingData && {
        onClick: () => {
          track(trackingData)
        }
      }),
      ...(hasLoginModalAction && {
        onClick: () => {
          triggerLoginModal({modalProps: {initialScreen: loginScreenId, trackingOrigin: HIT_ORIGINS.TOP_BAR_BUTTON}})
        }
      })
    }
  }

  /**
   * Prepare cta navigation section data for sui-topbar-user
   */
  const _getNavCTA = data => {
    return _mapData(data)
  }

  const getImagesUrlsAdapted = useImageUrlAdapter()

  /**
   * Prepare user navigation section data for sui-topbar-user
   */
  const _getNavUser = userConfig => {
    const USER_DEFAULT_AVATAR_URL = domain.get('config').get('USER_DEFAULT_AVATAR_URL')
    const {USER_AVATAR_SIZE} = domain.get('config').get('COMMON')

    let adaptedAvatar

    if (user) {
      const {avatar: userAvatar, name} = user

      const totalNotifications = Object.values(notifications).reduce((acc, {count}) => acc + (count || 0), 0)

      const hasNotifications = notifications && Object.values(notifications).some(notification => notification > 0)

      if (userAvatar) {
        const {
          images: [{src: adaptedImageSource}]
        } = getImagesUrlsAdapted({
          images: [{src: userAvatar}],
          options: {
            imageWidth: USER_AVATAR_SIZE,
            imageType: imageUrlAdapterImageTypes.AVATAR,
            transformType: imageUrlAdapterTransformTypes.DEVICE_CROP
          }
        })

        adaptedAvatar = adaptedImageSource
      }

      const avatar = !userAvatar ? USER_DEFAULT_AVATAR_URL : adaptedAvatar

      return {
        ...(hasNotifications && {
          accessibleBadgeLabel:
            totalNotifications > 0
              ? `(${i18n.t(ACCESSIBLE_BADGE_LABEL_LITERAL, totalNotifications)})`.toLowerCase()
              : `(${i18n.t(ACCESSIBLE_BADGE_LABEL_UNKNOWN_NOTIFICATIONS_LITERAL)})`.toLowerCase()
        }),
        avatar,
        name,
        menu: userConfig.loggedIn.filter(_shouldBePrinted).map(_mapData),
        hasUserBadgeLabel: false
      }
    }

    return {
      avatar: USER_DEFAULT_AVATAR_URL,
      name: i18n.t('NAVIGATION_USER_LOGGED_OUT'),
      menu: userConfig.loggedOut.filter(_shouldBePrinted).map(_mapData),
      hasUserBadgeLabel: false
    }
  }

  /**
   * Prepare main navigation section data for sui-topbar-user
   */
  const _getNavMain = main => {
    return main.filter(_shouldBePrinted).map(({icon, label, menu}) => ({
      icon: icons[icon],
      label: i18n.t(label),
      menu: menu.filter(_shouldBePrinted).map(({title, links}) => ({
        title: title ? i18n.t(title) : null,
        links: links.filter(_shouldBePrinted).map(_mapData)
      })),
      arrowButtonIcon: IconChevronDown
    }))
  }

  const _handleOnToggle = ({isOpen}) => {
    setIsOpen(isOpen)
    isOpen && track({event: 'Menu Opened'})
  }

  const navCTAData = _getNavCTA(cTA)
  const navCTALoginData = !user && withLoginButton ? _getNavCTA(cTALogin) : null

  // TopbarUser's `customContent` slot fully replaces its own navCTA/navCTALogin/
  // navCTASecondary rendering, and wraps what we pass in a `display: block` div —
  // so we compose our own flex row here or the CTAs stack and overflow the bar.
  const topbarCustomContent = (
    <div className={`${BASE_CLASS}-customContent`}>
      {customContent}
      <div className={`${BASE_CLASS}-ctaGroup`}>
        {navCTALoginData && (
          <Button
            {...CTA_SHARED_PROPS}
            variant={BUTTON_VARIANTS.OUTLINE}
            data-testid={CTA_LOGIN_TEST_ID}
            onClick={navCTALoginData.onClick}
          >
            {navCTALoginData.text}
          </Button>
        )}
        {/* data-testid is the e2e hook: qa/e2e/fixtures/constants/selectors/index.js → TOPBAR_CTA_LINK */}
        <Button
          {...CTA_SHARED_PROPS}
          variant={BUTTON_VARIANTS.SOLID}
          data-testid={CTA_TEST_ID}
          href={navCTAData.url}
          target={navCTAData.target}
          onClick={navCTAData.onClick}
        >
          {navCTAData.text}
        </Button>
        {navCTASecondary}
      </div>
    </div>
  )

  return (
    <header className={baseClassNames} data-testid={TEST_ID_SHARED_TOPBAR}>
      <TopbarUser
        brand={BRAND}
        customContent={topbarCustomContent}
        elementsToKeepScrollOnToggleMenu={elementsToKeepScrollOnToggleMenu}
        linkFactory={linkFactory}
        navButton={navButton}
        navMain={_getNavMain(navigationItems)}
        navUser={_getNavUser(userConfig)}
        onToggle={_handleOnToggle}
        shouldDisplayNavUser={showNavUserOnDesktop && _shouldDisplayNavUser(userConfig)}
        shouldDisplayToggle={showToggleNavOnMobile}
        showBrandIcon={showBrandIcon}
        title={title ? <TitleBasic size={titleBasicSizes.xs} title={title} /> : <></>}
        toggleAriaProps={{
          'aria-label': i18n.t('TOPBAR.TOGGLE_BUTTON.ARIA_LABEL')
        }}
        toggleIcon={icons.menu}
      />
    </header>
  )
}

SharedTopbar.displayName = 'SharedTopbar'

SharedTopbar.propTypes = {
  brandHeadingTag: PropTypes.oneOf(Object.values(topbarUserHeadingTags)),
  brandLogoAlt: PropTypes.string,
  /**
   * Extra content rendered alongside the CTA buttons, before them, inside
   * TopbarUser's customContent slot (see topbarCustomContent above).
   */
  customContent: PropTypes.node,
  design: PropTypes.oneOf(Object.values(DESIGNS)),
  elementsToKeepScrollOnToggleMenu: PropTypes.arrayOf(PropTypes.string),
  isHeaderFlush: PropTypes.bool,
  linkFactory: PropTypes.func,
  mainNavigationItems: PropTypes.array,
  maxContentWidth: PropTypes.string,
  navButton: PropTypes.node,
  navCTASecondary: PropTypes.node,
  showBrandIcon: PropTypes.bool,
  showNavUserOnDesktop: PropTypes.bool,
  showToggleNavOnMobile: PropTypes.bool,
  title: PropTypes.string,
  withLoginButton: PropTypes.bool
}

export {
  DESIGNS as sharedTopbarDesigns,
  topbarUserHeadingTags as sharedTopbarHeadingTags,
  MAX_CONTENT_WIDTHS as sharedTopbarMaxContentWidths
}
export default SharedTopbar
